import {
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  DOCTOR_VISIT_STATUS_READY,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";
import {
  getEditPendingVisitExpiresAt,
  getIsEditingPendingVisit,
} from "~/visits/utils/visitCookies";

export const PENDING_VISIT_EDIT_TTL_MS = 15 * 60 * 1000;

const VISIT_TYPE_TO_CONSULTATION_TYPE_ABBR = {
  [VISIT_TYPE_PERFORMANCE_ANXIETY]:
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  [VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  [VISIT_TYPE_GENERALIZED_ANXIETY]:
    CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  [VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  [VISIT_TYPE_INSOMNIA]: CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  [VISIT_TYPE_INSOMNIA_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
};

const CONSULTATION_TYPE_ABBR_TO_VISIT_TYPE = {
  [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY]:
    VISIT_TYPE_PERFORMANCE_ANXIETY,
  [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP]:
    VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
  [CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY]:
    VISIT_TYPE_GENERALIZED_ANXIETY,
  [CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP]:
    VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA]: VISIT_TYPE_INSOMNIA,
  [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP]:
    VISIT_TYPE_INSOMNIA_FOLLOW_UP,
};

// Statuses considered "pending" from the patient's perspective — anything where the visit has
// been submitted but the final prescription isn't out the door yet. PRAD (Pending Review Admin)
// is included because the patient still sees these in the "Thanks for completing your visit"
// PendingVisitCard and the editability rules should match that UI grouping.
const PENDING_STATUSES = [
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_READY,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
];

// Statuses where a submitted visit is still being worked. Broader than PENDING_STATUSES (which
// also gates patient editability): a visit awaiting Persona ID verification (PV) or awaiting the
// patient's reply to the doctor (PR) is in progress but NOT patient-editable. Used by the
// portal's follow-up/upgrade CTA suppression (a PV visit still suppresses those in its family;
// the Explore "Start Visit" cards and upsells gate on CHECKOUT_BLOCKING_STATUSES instead).
// Mirrors DoctorVisitStatusesInProgress on the Go side (app/domain_types.go) — keep in sync.
const IN_PROGRESS_STATUSES = [
  ...PENDING_STATUSES,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
];

// The in-progress statuses that BLOCK starting/checking out another visit in the same family:
// the earlier visit is queued for or mid clinical review, so completing a second one would charge
// the patient twice at doctor completion. PV is deliberately absent — a PV visit is a patient who
// abandoned the ID-verification step, and a new checkout in the family SUPERSEDES it (the backend
// closes it as duplicate when the new checkout succeeds; see CompleteCheckout on the Go side).
// Mirrors DoctorVisitStatusesBlockingNewCheckout (app/domain_types.go) — keep in sync.
const CHECKOUT_BLOCKING_STATUSES = [
  ...PENDING_STATUSES,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
];

// An Awaiting Patient Reply (PR) visit only counts as in progress for this long after it was
// created. Past that it is a question the patient never answered, not live clinical work: a new
// visit in the family supersedes (closes) it instead of being blocked by it — a 3-year-old PR
// insomnia visit was stopping patients from ever starting a new sleep visit.
// Mirrors DoctorVisitAwaitingReplyBlockingWindow (app/domain_types.go) — keep in sync.
export const AWAITING_REPLY_BLOCKING_WINDOW_DAYS = 90;

// isStaleAwaitingReplyVisit reports whether visit is Awaiting Patient Reply and was created more
// than AWAITING_REPLY_BLOCKING_WINDOW_DAYS before now. A missing or unparseable visitDate is
// never stale (an unknown age keeps blocking, matching the Go side).
export function isStaleAwaitingReplyVisit(visit, now = Date.now()) {
  if (visit?.status !== DOCTOR_VISIT_STATUS_AWAITING_REPLY) return false;
  const created = Date.parse(visit.visitDate);
  if (Number.isNaN(created)) return false;
  return (
    now - created > AWAITING_REPLY_BLOCKING_WINDOW_DAYS * 24 * 60 * 60 * 1000
  );
}

export function consultationTypeForVisitType(visitType) {
  return VISIT_TYPE_TO_CONSULTATION_TYPE_ABBR[visitType] || "";
}

export function visitTypeForConsultationType(consultationType) {
  return CONSULTATION_TYPE_ABBR_TO_VISIT_TYPE[consultationType] || "";
}

// VISIT_TYPE_FAMILIES groups visit types treated as one clinical "vertical" for the
// duplicate-in-progress check: performance + generalized anxiety share treatment (PA/PAF/GA/GAF),
// and insomnia is its own family (IN/INF). Mirrors DoctorVisitTypeFamily on the Go side
// (app/domain_types.go) — keep the two in sync.
const VISIT_TYPE_FAMILIES = [
  [
    VISIT_TYPE_PERFORMANCE_ANXIETY,
    VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
    VISIT_TYPE_GENERALIZED_ANXIETY,
    VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  ],
  [VISIT_TYPE_INSOMNIA, VISIT_TYPE_INSOMNIA_FOLLOW_UP],
];

// visitTypeFamily returns all visit types in the same clinical family as visitType (anxiety or
// sleep). Returns a single-element array for an unrecognized type, or [] for an empty input.
export function visitTypeFamily(visitType) {
  for (const family of VISIT_TYPE_FAMILIES) {
    if (family.includes(visitType)) return family;
  }
  return visitType ? [visitType] : [];
}

// getPendingVisitInFamily returns the first in-progress visit (status P/R/PRAD/PV/PR — a PR visit
// only within AWAITING_REPLY_BLOCKING_WINDOW_DAYS of its creation) whose visit
// type is in the same clinical family as visitType, or null. Used by the portal's CTA gating
// (suppress follow-up/upsell CTAs while any visit in the family is in progress). excludeVisitID
// (optional) skips the flow's own visit — the INF shortcut creates it in status "P" mid-flow,
// which would otherwise self-match (mirrors excludeVisitID on the Go-side guard).
export function getPendingVisitInFamily(visits, visitType, excludeVisitID) {
  return findVisitInFamilyByStatus(
    visits,
    visitType,
    IN_PROGRESS_STATUSES,
    excludeVisitID,
  );
}

// getBlockingVisitInFamily returns the first visit that BLOCKS starting another order in the
// family (status P/R/PRAD/PR — not PV, nor a PR visit older than
// AWAITING_REPLY_BLOCKING_WINDOW_DAYS, which a new checkout supersedes instead), or null. Used by
// the funnel's pending-visit redirect guard so a patient whose only in-family visit is stalled in
// Pending Verification can start over (mirrors CompleteCheckout's partition on the Go side).
export function getBlockingVisitInFamily(visits, visitType, excludeVisitID) {
  return findVisitInFamilyByStatus(
    visits,
    visitType,
    CHECKOUT_BLOCKING_STATUSES,
    excludeVisitID,
  );
}

function findVisitInFamilyByStatus(
  visits,
  visitType,
  statuses,
  excludeVisitID,
) {
  if (!visits || !visitType) return null;
  const family = visitTypeFamily(visitType);
  return (
    visits.find(
      (v) =>
        family.includes(v.visitType) &&
        statuses.includes(v.status) &&
        !isStaleAwaitingReplyVisit(v) &&
        (!excludeVisitID || v.id !== excludeVisitID),
    ) || null
  );
}

export function getAnyPendingVisit(visits) {
  if (!visits) return null;
  return visits.find((v) => PENDING_STATUSES.includes(v.status)) || null;
}

// buildPendingVisitsByType returns a map of visit_type → array of visit IDs for visits in any
// "pending" status (P, R, PRAD). Iteration order follows the input order (visits are sorted
// short_id DESC by the GraphQL resolver, so byType[type][0] is the most recent pending visit
// of that type).
export function buildPendingVisitsByType(visits) {
  const byType = {};
  if (!visits) return byType;
  for (const v of visits) {
    if (PENDING_STATUSES.includes(v.status)) {
      if (!byType[v.visitType]) byType[v.visitType] = [];
      byType[v.visitType].push(v.id);
    }
  }
  return byType;
}

export function hasPendingVisitOfType(pendingByType, visitType) {
  return !!(pendingByType && pendingByType[visitType]?.length);
}

export function isVisitPendingPrimary(pendingByType, visit) {
  if (!pendingByType || !visit) return false;
  const ids = pendingByType[visit.visitType];
  return !!(ids && ids[0] === visit.id);
}

// A visit's order item is "flavored" when its subcategory field is set OR its productSlug contains
// "flavored". The subcategory carries the chosen flavor (e.g. "tangerine"); productSlug guards
// against the rare case where the subcategory hasn't been chosen yet.
export function hasFlavoredOrderItem(visit) {
  if (!visit || !visit.orderItems) return false;
  return visit.orderItems.some(
    (oi) =>
      (oi.subcategory && oi.subcategory.length > 0) ||
      (oi.productSlug && oi.productSlug.indexOf("flavored") !== -1),
  );
}

export function isInPendingEditFlow(checkoutSessionID) {
  if (!checkoutSessionID) return false;
  return getIsEditingPendingVisit(checkoutSessionID);
}

export function isPendingEditExpired(checkoutSessionID) {
  const expiresAt = getEditPendingVisitExpiresAt(checkoutSessionID);
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
}

// editPendingPharmacyError returns a user-facing message when a pending-visit edit would submit a
// pharmacy-pickup (visit-only) order without a selected pharmacy, or "" when the submission is OK.
// It mirrors the server-side guard (ValidateEditPendingPayload) so the patient gets immediate
// feedback instead of a 400. A pharmacy-pickup order is one whose order type contains "visit-only"
// (matching isPharmacyPickupOrderType in sharedUtils and IsVisitOnlyOrderType on the Go side).
export function editPendingPharmacyError(orderType, pharmacyJSON) {
  const isPickup =
    typeof orderType === "string" && orderType.includes("visit-only");
  if (!isPickup) return "";
  const hasPharmacy =
    !!pharmacyJSON &&
    typeof pharmacyJSON === "object" &&
    typeof pharmacyJSON.pharmacyName === "string" &&
    pharmacyJSON.pharmacyName.trim().length > 0;
  return hasPharmacy
    ? ""
    : "Please select a pharmacy before continuing for pharmacy-pickup orders.";
}
